import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebase";
import profile from "../../assets/up-icons/profile.png";
import { revertTheme } from "../../utils/Utility.js";

const Navbar = ({ isEmbedded, navbarButtons }) => {
  const [user] = useAuthState(auth);
  const [imgSrc, setImgSrc] = useState(user?.photoURL || profile);

  // Initialize theme state with stored value immediately
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme = sessionStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    return storedTheme === "dark" || (!storedTheme && prefersDark);
  });

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // If the user object updates and doesn't have a photoURL, force the fallback.
    if (!user?.photoURL) {
      setImgSrc(profile);
    }
  }, [user]);

  // Apply theme on component mount and when isDarkMode changes
  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  const isLoginRoute = location.pathname === "/login";

  const handleLogout = async () => {
    try {
      revertTheme();
      await auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    sessionStorage.setItem("theme", newMode ? "dark" : "light");
  };

  return (
    <>
      {/* Main Navbar (render only if NOT embedded) */}
      {!isEmbedded && (
        <nav className="bg-tier-primary dark:bg-tier-primary-dark shadow-lg sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link
              to="/"
              className="flex items-end text-color hover:text-dark-alt transition duration-300 space-x-4"
            >
              <picture>
                <source type="image/webp" srcSet={"/icons/logo.webp"} />
                <source type="image/png" srcSet={"/icons/logo.png"} />
                <img
                  src={"/icons/logo.png"}
                  alt="Tools by UP-Hub"
                  className="w-8 h-auto mx-auto"
                />
              </picture>

              <span className="text-2xl font-luthier font-extrabold">
                Tools
              </span>
            </Link>
            <div className="flex flex-row space-x-4">
              {!isLoginRoute && (
                <>
                  <button
                    onClick={toggleDarkMode}
                    className="px-2 pt-2 hover:bg-white/10 rounded-full"
                  >
                    <span className="material-symbols-outlined">
                      {isDarkMode ? "dark_mode" : "light_mode"}
                    </span>
                  </button>
                  {user ? (
                    <div className="flex flex-row items-center gap-4">
                      <img
                        src={imgSrc}
                        alt="User"
                        className="w-8 h-8 rounded-full border bg-white"
                        onError={(e) => {
                          if (e.target.src !== profile) {
                            setImgSrc(profile);
                          }
                        }}
                      />

                      <button
                        onClick={handleLogout}
                        className="btn-action flex items-center"
                      >
                        <span className="material-symbols-outlined mr-2">
                          logout
                        </span>
                        Logout
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="flex items-center bg-teal-500 text-color px-4 py-2 rounded-lg shadow hover:bg-teal-600 focus:outline-none focus:ring-4 focus:ring-teal-300 transition duration-300"
                    >
                      <span className="material-symbols-outlined mr-2">
                        login
                      </span>
                      Login
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </nav>
      )}

      {/* New Section Below Navbar for Page-Specific Buttons */}
      {navbarButtons.length > 0 && (
        <div
          className={`bg-tier-primary dark:bg-tier-primary-dark py-3 sticky left-0 right-0 z-30 ${
            isEmbedded ? "top-0" : "top-20"
          }`}
        >
          <div className="container mx-auto px-4 flex space-x-2 justify-center">
            {navbarButtons.map((button, index) => (
              <button
                key={index}
                onClick={button.onClick}
                className={`px-4 py-1 rounded-lg text-color hover:text-tier-primary border dark:border-gray-600 hover:border-dark-alt hover:bg-tier-secondary shadow transition duration-300 flex gap-2 items-center ${button.className}`}
              >
                {button.icon ? (
                  <span className="material-symbols-outlined text-large">
                    {button.icon}
                  </span>
                ) : (
                  <span className="material-symbols-outlined text-large invisible">
                    placeholder
                  </span>
                )}
                <span className="hidden lg:inline-block">
                  {typeof button.label === "function"
                    ? button.label()
                    : button.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
